/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import { filtered, Nullable } from '@e2m-customer-portal/shared';
import { alpha, ToggleButton as MuiToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { cloneElement, Key, ReactElement, useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui-compat';

type ColorType = 'primary' | 'error';

const useStyles = makeStyles()((theme) => {
  const forColor = (color: ColorType) =>
    ({
      fill: 'currentColor',
      color: theme.palette[color].main,
      borderColor: theme.palette[color].main,
      transition: theme.transitions.create(['border-color', 'background-color']),
      '&:hover': {
        borderColor: theme.palette[color].main,
        backgroundColor: alpha(theme.palette[color].main, theme.palette.action.hoverOpacity),
      },
      '&.Mui-selected': {
        color: theme.palette[color].main,
        borderColor: theme.palette[color].main,
        backgroundColor: alpha(theme.palette[color].main, theme.palette.action.hoverOpacity * 2),
        '&:hover': {
          backgroundColor: alpha(theme.palette[color].main, theme.palette.action.hoverOpacity * 4),
        },
      },
    }) as const;

  return {
    root: {},
    colorPrimary: forColor('primary'),
    colorError: forColor('error'),
  };
});

export interface ToggleButtonProps<T extends Key> {
  /**
   * An additional class-name to apply to this component.
   */
  className?: string;

  /**
   * The color to use for the buttons. Defaults to `primary`.
   */
  color?: ColorType;

  /**
   * The currently selected value. Should be one of the identifiers in the `options` list.
   */
  value?: T;

  /**
   * Gets called when another button is selected. The given parameter is the identifier of the new button.
   */
  onChange?: (value: T) => void;
  /**
   * Flag to disable the Toggle
   */
  disabled?: boolean;
  /**
   * String for data test id
   */
  testId?: string;

  /**
   * The list of buttons to display. Each child component must have a `key` property that corresponds to the value.
   */
  children?: ReactElement | ReactElement[];
}

function childArray<T>(children: Nullable<T> | Nullable<T>[]): T[] {
  return filtered(Array.isArray(children) ? children : [children]);
}

export function ToggleButton<T extends string>({
  className,
  color = 'primary',
  value,
  onChange,
  disabled,
  testId,
  children: childrenProp,
}: ToggleButtonProps<T>): ReactElement<ToggleButtonProps<T>> | null {
  const { classes, cx } = useStyles();
  const children = childArray(childrenProp);
  const [state, setState] = useState<T>(value ?? (children[0]?.key as T));

  const handleChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, newValue: T) => {
      if (newValue) {
        onChange?.(newValue);
        if (value == null) {
          setState(newValue);
        }
      }
    },
    [onChange, value],
  );

  if (children.length === 0) {
    return null;
  }

  return (
    <ToggleButtonGroup
      disabled={disabled}
      className={cx(classes.root, className, 'w-[100%] md:w-auto mx-2 md:mx-0')}
      classes={{
        grouped: cx({
          [classes.colorPrimary]: color === 'primary',
          [classes.colorError]: color === 'error',
        }),
      }}
      exclusive
      value={value ?? state}
      onChange={handleChange}
      data-testid={testId}
    >
      {children.map((child: React.JSX.Element) => {
        if (child.type === Tooltip) {
          // Special case: If the child has a tooltip, wrap the tooltip around the
          // button instead, so it activates on hovering the button, not only the icon
          return cloneElement(
            child,
            child.props,
            <MuiToggleButton
              className="h-10 p-[0.1875rem]"
              value={child.key as Key}
              selected={child.key === (value ?? state)}
            >
              <>{child.props.children}</>
            </MuiToggleButton>,
          );
        }

        return (
          <MuiToggleButton className="h-10 w-full" key={child.key} value={child.key as Key}>
            <>{child}</>
          </MuiToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
}
